/* eslint class-methods-use-this: ["error",
{ "exceptMethods": ["isAuthenticated", "setSession"] }] */

import auth0 from 'auth0-js';
import _ from 'lodash';
import axios from 'axios';
import { auth as authConfig } from '@/config/config';
import httpService from '@/api/HttpService';
import { logAction, logglyTags } from '@/util';
import logActions from '@/data/static/logActions';
import { resetState } from '@/store';

export default class AuthService {
  static register(registerForm) {
    return httpService.postV2('users/register', registerForm);
  }

  static setSession(authResult) {
    const expiresAt = JSON.stringify((authResult.expiresIn * 1000) + new Date().getTime());
    sessionStorage.setItem('access_token', authResult.accessToken);
    sessionStorage.setItem('id_token', authResult.idToken);
    sessionStorage.setItem('expires_at', expiresAt);
  }

  static checkMfaRequired(loginError) {
    return _.get(loginError, 'statusCode', false) === 403 && _.get(loginError, 'code', false) === 'mfa_required';
  }

  static challengeMfa() {
    return httpService.post('/mfa/challenge', {
      mfaToken: sessionStorage.getItem('mfa_token'),
    }).then((response) => {
      sessionStorage.setItem('oob_code', response.oob_code);
    });
  }

  static handleMfa({ mfaCode }) {
    return axios({
      method: 'POST',
      url: `https://${authConfig.domain}/oauth/token`,
      headers: {
        'Content-Type': 'application/json',
      },
      data: {
        grant_type: 'http://auth0.com/oauth/grant-type/mfa-oob',
        mfa_token: sessionStorage.getItem('mfa_token'),
        oob_code: sessionStorage.getItem('oob_code'),
        binding_code: mfaCode,
        client_id: authConfig.clientId,
      },
    }).then((res) => {
      const {
        access_token,
        id_token,
        expires_in,
      } = res.data;
      this.setSession({
        expiresIn: expires_in,
        accessToken: access_token,
        idToken: id_token,
      });
      sessionStorage.removeItem('mfa_token');
      sessionStorage.removeItem('oob_code');
    }).catch((e) => {
      throw _.get(e, ['response', 'data']);
    });
  }

  static logIn({ email, password }) {
    const webAuth = new auth0.WebAuth({
      domain: authConfig.domain,
      clientID: authConfig.clientId,
      audience: authConfig.audience,
      responseType: 'token id_token',
    });
    return new Promise((resolve, reject) => {
      webAuth.client.login({
        username: email,
        password,
        realm: authConfig.connection,
        scope: 'openid email',
      }, (err, authResult) => {
        if (err) {
          if (this.checkMfaRequired(err)) {
            sessionStorage.setItem('mfa_token',
          _.get(err, ['original', 'response', 'body', 'mfa_token']));
            this.challengeMfa().then(() => {
              resolve({ needsMfa: true });
            });
            return;
          }
          logAction({
            action: logActions.LOGIN_FAILED.name,
            tags: [logglyTags.auth0],
            message: `User failed to log in as ${email}`,
            level: 'info',
          });
          reject(err);
          return;
        }
        if (authResult && authResult.accessToken && authResult.idToken) {
          this.setSession(authResult);
          resolve(authResult);
        } else {
          logAction({
            action: logActions.LOGIN_FAILED.name,
            tags: [logglyTags.auth0],
            message: `User failed to log in as ${email}`,
            level: 'info',
          });
          reject(new Error('Missing token'));
        }
      });
    });
  }

  static logOut() {
    resetState();
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('id_token');
    sessionStorage.removeItem('expires_at');
    return Promise.resolve();
  }

  static resetPassword({ email }) {
    const webAuth = new auth0.WebAuth({
      domain: authConfig.domain,
      clientID: authConfig.clientId,
      audience: authConfig.audience,
      responseType: 'token id_token',
    });
    const { connection } = authConfig;
    return new Promise((resolve, reject) => {
      webAuth.changePassword({
        connection,
        email,
      }, (err, resp) => {
        if (err) {
          reject(err);
        } else {
          logAction({
            message: `Password reset requested ${email}`,
            action: logActions.PASSWORD_RESET.name,
            tags: [logglyTags.auth0],
            level: 'info',
          });
          resolve(resp);
        }
      });
    });
  }
}



// WEBPACK FOOTER //
// ./src/api/AuthService.js